import { Box, FormHelperText, InputAdornment, TextField, Typography } from "@mui/material";
import React from "react";
import { HankoHelpTooltip } from "../Tooltip/Tooltip";

export interface StandardAttributeField {
  key: string
  label: string
  helperText?: React.ReactNode
}

interface Props {
  fields: StandardAttributeField[]
  helperText?: React.ReactNode
  value: Record<string, string>
  onChange: (next: Record<string, string>) => void
}

export const isStandardAttributeMapEmpty = (fields: StandardAttributeField[], value: Record<string, string>): boolean => {
  return fields.every(({ key }) => !value[key])
}

export const standardAttributeMapEquals = (fields: StandardAttributeField[], a: Record<string, string>, b: Record<string, string>): boolean => {
  return fields.every(({ key }) => (a[key] ?? "") === (b[key] ?? ""))
}

// Plucks just the known standard-field keys out of a raw value (e.g. a SAML AttributeMap,
// which also carries an unrelated nested `custom` object) into a plain Record<string,string>
// this component can work with. Loosely typed on purpose - callers pass differently-shaped
// wire types (AttributeMap, CustomThirdPartyProvider.attribute_mapping, ...).
export const extractStandardFields = (fields: StandardAttributeField[], value?: Record<string, any>): Record<string, string> => {
  const result: Record<string, string> = {}
  fields.forEach(({ key }) => {
    const v = value?.[key]
    if (typeof v === "string" && v !== "") {
      result[key] = v
    }
  })
  return result
}

// SAML's config.AttributeMap only actually reads name/given_name/family_name/picture (display)
// and email/email_verified (account linking) - see thirdparty.Claims.ProviderProfile and
// thirdparty/linking.go. The other 12 AttributeMap fields are faithfully captured if mapped,
// but nothing in Hanko ever reads them back, so this UI doesn't offer them.
export const samlAttributeMapHelperText = "Maps this connection's SAML attributes onto Hanko's standard profile " +
  "fields. Each field's label (Name, Email, ...) is the Hanko field being set; the value you enter is the " +
  "SAML attribute name (or URI) from your IdP to read it from. Leave a field blank to use Hanko's default for " +
  "it - hover the field's own help icon for that default's exact value."

export const samlStandardAttributeFields: StandardAttributeField[] = [
  {
    key: "name",
    label: "Name",
    helperText: "Defaults to http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name if left blank. If " +
      "this connection's name contains \"auth0\", Hanko uses http://schemas.auth0.com/name instead."
  },
  {
    key: "given_name",
    label: "Given name",
    helperText: "Defaults to http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname if left blank."
  },
  {
    key: "family_name",
    label: "Family name",
    helperText: "Defaults to http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname if left blank."
  },
  {
    key: "email",
    label: "Email",
    helperText: "Defaults to http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress if left " +
      "blank (including for Auth0 connections)."
  },
  {
    key: "email_verified",
    label: "Email verified",
    helperText: "No default - stays unverified unless mapped here or \"Skip email verification\" below is " +
      "enabled, unless this connection's name contains \"auth0\", which defaults to " +
      "http://schemas.auth0.com/email_verified."
  },
  {
    key: "picture",
    label: "Picture",
    helperText: "No default - stays empty unless mapped here, unless this connection's name contains " +
      "\"auth0\", which defaults to http://schemas.auth0.com/picture."
  },
]

// Same 6 fields as samlStandardAttributeFields, for the same reason (see there) - OIDC just
// gets its defaults differently: AttributeMapping only renames a claim when a mapping entry
// exists for it, so an unmapped standard field simply falls through to the provider's own
// same-named claim if it sends one (which spec-compliant OIDC providers do by default).
export const oidcAttributeMapHelperText = "Maps this provider's own claims onto Hanko's standard profile " +
  "fields. Each field's label (Name, Email, ...) is the Hanko field being set; the value you enter is the " +
  "provider's own claim name to read it from. Leave a field blank to use Hanko's default for it - hover the " +
  "field's own help icon for that default's exact value."

export const oidcStandardAttributeFields: StandardAttributeField[] = [
  { key: "name", label: "Name", helperText: "Defaults to this provider's own \"name\" claim if left blank." },
  { key: "given_name", label: "Given name", helperText: "Defaults to this provider's own \"given_name\" claim if left blank." },
  { key: "family_name", label: "Family name", helperText: "Defaults to this provider's own \"family_name\" claim if left blank." },
  { key: "email", label: "Email", helperText: "Defaults to this provider's own \"email\" claim if left blank." },
  { key: "email_verified", label: "Email verified", helperText: "Defaults to this provider's own \"email_verified\" claim if left blank." },
  { key: "picture", label: "Picture", helperText: "Defaults to this provider's own \"picture\" claim if left blank." },
]

export const StandardAttributeMapFields = (props: Props) => {
  const { fields, helperText, value, onChange } = props

  const onFieldChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, [key]: e.target.value })
  }

  return <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography variant="subtitle2">Attribute mapping</Typography>
    {helperText !== undefined ? <FormHelperText sx={{ mt: -1, mb: 1 }}>{helperText}</FormHelperText> : null}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {fields.map((field) => (
        <TextField key={field.key} label={field.label} fullWidth size="small" value={value[field.key] ?? ""}
                   onChange={onFieldChange(field.key)}
                   InputProps={field.helperText !== undefined ? {
                     endAdornment: <InputAdornment position="end"><HankoHelpTooltip title={field.helperText} /></InputAdornment>,
                   } : undefined} />
      ))}
    </Box>
  </Box>
}
